import type { AccountId, AgentTask, DistributionPlatform, PlatformWeight, TaskStatus, TaskType, Topic } from "../types";
import { DISTRIBUTION_PLATFORMS, TASK_STATUSES, TASK_TYPES } from "../types";
import { isAccountId, isSpokenPlatform, topicAccount } from "./accounts";
import { formatWhen } from "../lib/format";

export const DIRECTION_PRESETS = ["AI 提效", "最近热点", "职场", "副业"] as const;

export const GENERATE_TYPES = ["生成标题", "生成文案", "生成图文帖子", "生成口播稿"] as const;

export type GenerateType = (typeof GENERATE_TYPES)[number];

export interface FindTopicInput {
  id?: string;
  title: string;
  reason?: string;
  viralReason?: string;
  recommendRank?: number | null;
  sourceLinks?: string[];
  sourcePlatform?: string;
  platforms?: string[];
  notionUrl?: string;
}

export function isTaskType(value: string | null | undefined): value is TaskType {
  return Boolean(value && (TASK_TYPES as readonly string[]).includes(value));
}

export function isTaskStatus(value: string | null | undefined): value is TaskStatus {
  return Boolean(value && (TASK_STATUSES as readonly string[]).includes(value));
}

export function isDistributionPlatform(value: string): value is DistributionPlatform {
  return (DISTRIBUTION_PLATFORMS as readonly string[]).includes(value);
}

export function normalizePlatforms(values: readonly string[]): string[] {
  const next: string[] = [];
  for (const value of values) {
    const label = value.trim();
    if (isDistributionPlatform(label) && !next.includes(label)) next.push(label);
  }
  return next;
}

export function buildTask(
  input: {
    type: TaskType;
    accountId: AccountId;
    platforms?: readonly string[];
    weights?: readonly PlatformWeight[];
    direction?: string;
    count?: number;
    topicId?: string | null;
    subjectId?: string | null;
  },
  now: string,
  id: string,
): AgentTask {
  const platforms = normalizePlatforms(input.platforms ?? []);
  const count = clampCount(input.count);
  return {
    id,
    type: input.type,
    accountId: input.accountId,
    platforms,
    weights: input.weights ? weightsFor(platforms, input.weights) : evenWeights(platforms),
    direction: (input.direction ?? "").trim().slice(0, 400),
    count,
    topicId: input.topicId ?? null,
    subjectId: input.subjectId?.trim() ? input.subjectId.trim().slice(0, 80) : null,
    claimedBy: null,
    status: "排队",
    result: "",
    createdAt: now,
    startedAt: null,
    completedAt: null,
    updatedAt: now,
  };
}

export function evenWeights(platforms: readonly string[]): PlatformWeight[] {
  const labels = normalizePlatforms(platforms);
  if (!labels.length) return [];
  const base = Math.floor(100 / labels.length);
  let extra = 100 - base * labels.length;
  return labels.map((platform) => {
    const weight = base + (extra > 0 ? 1 : 0);
    if (extra > 0) extra -= 1;
    return { platform, weight };
  });
}

/** Keep one weight and rescale the others so the selected platforms still sum to 100. */
export function setPlatformWeight(weights: readonly PlatformWeight[], platform: string, next: number): PlatformWeight[] {
  if (!weights.some((item) => item.platform === platform)) return [...weights];
  if (weights.length <= 1) return weights.map((item) => ({ platform: item.platform, weight: 100 }));
  const target = Math.max(0, Math.min(100, Math.round(next)));
  const others = weights.filter((item) => item.platform !== platform);
  const rest = 100 - target;
  const sum = others.reduce((total, item) => total + item.weight, 0);
  const exact = others.map((item) => (sum === 0 ? rest / others.length : (item.weight / sum) * rest));
  const floors = exact.map((value) => Math.floor(value));
  let left = rest - floors.reduce((total, value) => total + value, 0);
  const order = exact
    .map((value, index) => ({ index, frac: value - (floors[index] ?? 0) }))
    .sort((leftItem, rightItem) => rightItem.frac - leftItem.frac || leftItem.index - rightItem.index);
  const ints = [...floors];
  for (const item of order) {
    if (left <= 0) break;
    ints[item.index] = (ints[item.index] ?? 0) + 1;
    left -= 1;
  }
  const assigned = new Map(others.map((item, index) => [item.platform, ints[index] ?? 0]));
  return weights.map((item) =>
    item.platform === platform ? { platform, weight: target } : { platform: item.platform, weight: assigned.get(item.platform) ?? 0 },
  );
}

export function weightsFor(platforms: readonly string[], current: readonly PlatformWeight[] | undefined): PlatformWeight[] {
  const labels = normalizePlatforms(platforms);
  if (!labels.length) return [];
  const known = new Map(
    (current ?? [])
      .filter((item) => labels.includes(item.platform) && Number.isFinite(item.weight))
      .map((item) => [item.platform, Math.max(0, item.weight)]),
  );
  if (labels.some((label) => !known.has(label))) return evenWeights(labels);
  return scaleWeights(labels.map((platform) => ({ platform, weight: known.get(platform) ?? 0 })));
}

/** What the assistant should use. An empty stored list means an even split. */
export function taskWeights(task: Pick<AgentTask, "platforms" | "weights">): PlatformWeight[] {
  if (task.weights?.length) return weightsFor(task.platforms, task.weights);
  return evenWeights(task.platforms);
}

function scaleWeights(weights: PlatformWeight[]): PlatformWeight[] {
  const sum = weights.reduce((total, item) => total + item.weight, 0);
  if (sum === 100) return weights.map((item) => ({ platform: item.platform, weight: Math.round(item.weight) }));
  if (sum <= 0) return evenWeights(weights.map((item) => item.platform));
  const exact = weights.map((item) => (item.weight / sum) * 100);
  const floors = exact.map((value) => Math.floor(value));
  let left = 100 - floors.reduce((total, value) => total + value, 0);
  const order = exact
    .map((value, index) => ({ index, frac: value - (floors[index] ?? 0) }))
    .sort((leftItem, rightItem) => rightItem.frac - leftItem.frac || leftItem.index - rightItem.index);
  const ints = [...floors];
  for (const item of order) {
    if (left <= 0) break;
    ints[item.index] = (ints[item.index] ?? 0) + 1;
    left -= 1;
  }
  return weights.map((item, index) => ({ platform: item.platform, weight: ints[index] ?? 0 }));
}

export function parseWeights(value: unknown): PlatformWeight[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const platform = (item as { platform?: unknown }).platform;
    const weight = (item as { weight?: unknown }).weight;
    if (typeof platform !== "string" || typeof weight !== "number" || !Number.isFinite(weight)) return [];
    return [{ platform, weight }];
  });
}

export function claimTask(task: AgentTask, now: string): AgentTask | null {
  if (task.status !== "排队") return null;
  return { ...task, status: "进行中", startedAt: now, updatedAt: now };
}

export function failTask(task: AgentTask, result: string, now: string): AgentTask | null {
  if (task.status !== "进行中" && task.status !== "排队") return null;
  return {
    ...task,
    status: "失败",
    result: result.trim().slice(0, 4000),
    completedAt: now,
    updatedAt: now,
  };
}

export function finishTask(task: AgentTask, result: string, now: string): AgentTask | null {
  if (task.status !== "进行中" && task.status !== "排队") return null;
  return {
    ...task,
    status: "已完成",
    result: result.trim().slice(0, 4000),
    completedAt: now,
    updatedAt: now,
  };
}

export function topicFromFind(task: AgentTask, input: FindTopicInput, now: string, id: string): Topic | null {
  const title = input.title.trim().slice(0, 500);
  if (!title) return null;
  const links = (input.sourceLinks ?? []).map((item) => item.trim()).filter(Boolean).slice(0, 12);
  const rank = typeof input.recommendRank === "number" && Number.isFinite(input.recommendRank) ? input.recommendRank : null;
  return {
    id,
    title,
    status: "待确认",
    batch: "",
    angle: task.direction,
    audience: "",
    sources: links.join("\n"),
    createdDate: now.slice(0, 10),
    notionUrl: (input.notionUrl ?? "").trim(),
    publishUrl: null,
    plannedPublishDate: null,
    plannedPublishAt: null,
    actualPublishAt: null,
    sourceAssistant: "热点监控助理",
    reason: (input.reason ?? "").trim().slice(0, 2000),
    heat: null,
    heatBasis: "",
    referenceUrl: links[0] ?? null,
    queueOrder: null,
    accountId: task.accountId,
    recommendRank: rank,
    viralReason: (input.viralReason ?? "").trim().slice(0, 2000),
    sourceLinks: links,
    sourcePlatform: (input.sourcePlatform ?? "").trim().slice(0, 80),
    sourceTaskId: task.id,
    platforms: normalizePlatforms(input.platforms?.length ? input.platforms : task.platforms),
    notes: "",
    notesUpdatedAt: null,
    lastEditedTime: now,
    updatedAt: now,
    inTrash: false,
    syncError: null,
  };
}

export function sortByRank<T extends Pick<Topic, "recommendRank" | "title">>(topics: readonly T[]): T[] {
  return [...topics].sort((left, right) => {
    const leftRank = typeof left.recommendRank === "number" ? left.recommendRank : Number.POSITIVE_INFINITY;
    const rightRank = typeof right.recommendRank === "number" ? right.recommendRank : Number.POSITIVE_INFINITY;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return left.title.localeCompare(right.title, "zh");
  });
}

export function oneLineReason(topic: { reason?: string | null }): string {
  const line = (topic.reason ?? "").trim().split("\n")[0]?.trim() ?? "";
  if (line.length <= 42) return line;
  return `${line.slice(0, 42)}…`;
}

export function sourceLinkList(topic: { sourceLinks?: readonly string[] | null }): string[] {
  return (topic.sourceLinks ?? []).filter((item) => typeof item === "string" && item.trim());
}

export function generateActions(accountOrPlatform: string): Array<{ type: GenerateType; label: string }> {
  if (accountOrPlatform === "douyin" || isSpokenPlatform(accountOrPlatform)) {
    return [
      { type: "生成标题", label: "只生成标题" },
      { type: "生成口播稿", label: "生成口播稿" },
    ];
  }
  return [
    { type: "生成标题", label: "只生成标题" },
    { type: "生成文案", label: "只生成文案" },
    { type: "生成图文帖子", label: "生成完整图文帖子" },
    { type: "生成口播稿", label: "生成口播稿" },
  ];
}

export function resultLabel(type: TaskType): string {
  if (type === "生成口播稿") return "口播稿";
  if (type === "生成标题") return "标题";
  if (type === "生成文案") return "文案";
  if (type === "生成图文帖子") return "图文帖子";
  return "结果";
}

export function shouldEnterReview(topic: Topic): boolean {
  return topic.status === "待确认" || topic.status === "已确认" || topic.status === "制作中";
}

export type TaskChip = "排队" | "执行中" | "失败";

export function taskChip(task: Pick<AgentTask, "status">): TaskChip | null {
  if (task.status === "排队") return "排队";
  if (task.status === "进行中") return "执行中";
  if (task.status === "失败") return "失败";
  return null;
}

export function activeTaskForTopic(tasks: readonly AgentTask[], topic: Pick<Topic, "id" | "sourceTaskId">): AgentTask | null {
  return tasks
    .filter((task) => taskChip(task) && (task.topicId === topic.id || (topic.sourceTaskId != null && task.id === topic.sourceTaskId)))
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0] ?? null;
}

export function orphanActiveTasks(tasks: readonly AgentTask[], topics: readonly Pick<Topic, "id" | "sourceTaskId">[]): AgentTask[] {
  const ids = new Set(topics.map((topic) => topic.id));
  const sources = new Set(topics.map((topic) => topic.sourceTaskId).filter((item): item is string => Boolean(item)));
  return tasks
    .filter((task) => taskChip(task) && !(task.topicId && ids.has(task.topicId)) && !sources.has(task.id))
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export function taskStatusLine(task: AgentTask, agentReady: boolean, covered = false): string {
  if (task.status === "排队") {
    if (covered) return "排队，等待执行者领取";
    if (agentReady) return "排队，没有执行者负责这类任务";
    return "已保存，还没有执行者负责这类任务，暂不会执行";
  }
  if (task.status === "进行中") {
    const detail = task.direction ? `${task.type} · ${task.direction}` : task.type;
    return `正在执行：${detail}`;
  }
  if (task.status === "失败") return `上次失败：${task.result.trim() || "没有写明原因"}`;
  if (task.result.trim()) return `已完成：${task.result.trim()}`;
  return `最近完成 ${formatWhen(task.completedAt ?? task.updatedAt)}`;
}

const ASSISTANT_OFFLINE = "尚未接通：正式版上线并设置密钥后，助理会自动领取任务";

export function assistantDeskLines(tasks: readonly AgentTask[], agentReady: boolean): Array<{ name: string; line: string }> {
  const groups: Array<{ name: string; types: TaskType[] }> = [
    { name: "热点监控助理", types: ["找选题", "找对标爆款", "拆解对标"] },
    { name: "小红书内容助理", types: ["生成标题", "生成文案", "生成图文帖子", "生成口播稿", "同步账号数据", "网感打分", "按建议重写"] },
  ];
  if (!agentReady) return groups.map((group) => ({ name: group.name, line: ASSISTANT_OFFLINE }));
  return groups.map((group) => ({
    name: group.name,
    line: deskLine(tasks.filter((task) => group.types.includes(task.type))),
  }));
}

function deskLine(tasks: readonly AgentTask[]): string {
  const running = tasks.find((task) => task.status === "进行中");
  if (running) {
    const detail = running.direction ? `${running.type} · ${running.direction}` : running.type;
    return `正在执行：${detail}`;
  }
  const finished = tasks
    .filter((task) => task.status === "失败" || task.status === "已完成")
    .sort((left, right) => (right.completedAt ?? right.updatedAt).localeCompare(left.completedAt ?? left.updatedAt));
  const latest = finished[0];
  if (!latest) return "空闲";
  if (latest.status === "失败") return `上次失败：${latest.result.trim() || "没有写明原因"}`;
  return `最近完成 ${formatWhen(latest.completedAt ?? latest.updatedAt)}`;
}

export function tasksForAccount(tasks: readonly AgentTask[], accountId: string): AgentTask[] {
  return tasks.filter((task) => topicAccount(task) === accountId);
}

export function clampCount(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 5;
  return Math.min(100, Math.max(1, Math.round(value)));
}

export function normalizeTask(value: unknown): AgentTask | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<AgentTask>;
  if (typeof raw.id !== "string" || !isTaskType(raw.type) || !isTaskStatus(raw.status)) return null;
  return {
    id: raw.id,
    type: raw.type,
    accountId: isAccountId(raw.accountId) ? raw.accountId : "xiaohongshu",
    platforms: Array.isArray(raw.platforms) ? normalizePlatforms(raw.platforms.filter((item): item is string => typeof item === "string")) : [],
    direction: typeof raw.direction === "string" ? raw.direction.slice(0, 400) : "",
    count: clampCount(typeof raw.count === "number" ? raw.count : 1),
    weights: Array.isArray(raw.weights) ? weightsFor(
      Array.isArray(raw.platforms) ? raw.platforms.filter((item): item is string => typeof item === "string") : [],
      parseWeights(raw.weights),
    ) : [],
    topicId: typeof raw.topicId === "string" ? raw.topicId : null,
    subjectId: typeof raw.subjectId === "string" && raw.subjectId.trim() ? raw.subjectId.trim().slice(0, 80) : null,
    claimedBy: typeof raw.claimedBy === "string" && raw.claimedBy.trim() ? raw.claimedBy.trim().slice(0, 80) : null,
    status: raw.status,
    result: typeof raw.result === "string" ? raw.result : "",
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : raw.updatedAt ?? "",
    startedAt: typeof raw.startedAt === "string" ? raw.startedAt : null,
    completedAt: typeof raw.completedAt === "string" ? raw.completedAt : null,
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : raw.createdAt ?? "",
  };
}
